<template>
    <b-card title="Admins">
  
        <!-- search input -->
        <div class="custom-search d-flex justify-content-between">
            <b-form-group class="justify-content-end">
                <div class="d-flex align-items-center">
                    <label class="mr-1">Search</label>
                    <b-form-input
                        v-model="searchTerm"
                        placeholder="Search"
                        type="text"
                        class="d-inline-block"
                    />
                </div>
            </b-form-group>
            <b-form-group v-if="this.showAdd">
                <div class="d-flex align-items-center">
                    <b-button
                        id="toggle-btn"
                        v-b-modal.modal-prevent-closing
                        v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                        variant="outline-primary"
                        @click="addNew()"
                    >
                        <feather-icon
                            icon="PlusIcon"
                            class="mr-50"
                        />
                        <span class="align-middle">Add New</span>
                    </b-button>
                </div>
            </b-form-group>
        </div>
    
        <!-- table -->
        <div v-if="!adminLoading && !adminError">
            <vue-good-table
                :columns="columns"
                :rows="rows"
                :rtl="direction"
                :search-options="{
                    enabled: true,
                    externalQuery: searchTerm 
                }"
                :pagination-options="{
                    enabled: true,
                    perPage:pageLength
                }"
                styleClass="vgt-table striped condensed no-outline"
            >
        
                <template
                    slot="table-row"
                    slot-scope="props"
                >
        
                    <!-- Column: Name -->
                    <span
                        v-if="props.column.field === 'fullName'"
                        class="text-nowrap"
                    >
                        <span class="text-nowrap">{{ props.row.first_name }} {{ props.row.last_name }}</span>
                    </span>

                    <!-- Column: Group -->
                    <span
                        v-else-if="props.column.field === 'group_name'"
                        class="text-nowrap"
                    >
                        <span class="text-nowrap" v-for="group in props.row.group" style="display: flex; flex-direction: column; margin: 5px 0px;">
                            <b-badge>
                                {{ group.name }}
                            </b-badge>
                        </span>
                    </span>
                    
                    <!-- Column: Index -->
                    <span v-else-if="props.column.field === 'index'">
                        <span>{{ props.row.originalIndex + 1 }}</span>
                    </span>

                    <!-- Column: Action -->
                    <span v-else-if="props.column.field === 'action'">
                        <span>
                            <b-button @click="edit(props.row)"
                                    v-b-modal.modal-prevent-closing
                                    v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                                    variant="primary" class="btn-icon rounded-circle mr-1"
                                    v-if="showEdit"
                            >
                                <feather-icon
                                    icon="Edit2Icon"
                                />
                            </b-button>
                            <b-button @click="deleteType(props.row)"
                                        id="delete-btn"
                                        v-b-modal.modal-delete
                                        v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                                        variant="primary" class="btn-icon rounded-circle"
                                        v-if="showDelete"
                            >
                                <feather-icon
                                    icon="TrashIcon"
                                />
                            </b-button>
                        </span>
                    </span>
            
                    <!-- Column: Common -->
                    <span v-else>
                        {{ props.formattedRow[props.column.field] }}
                    </span>
                </template>
        
                <!-- pagination -->
                <template
                    slot="pagination-bottom"
                    slot-scope="props"
                >
                    <div class="d-flex justify-content-between flex-wrap">
                        <div class="d-flex align-items-center mb-0 mt-1">
                            <span class="text-nowrap ">
                                Showing 1 to
                            </span>
                            <b-form-select
                                v-model="pageLength"
                                :options="['3','5','10','50','100']"
                                class="mx-1"
                                @input="(value)=>props.perPageChanged({currentPerPage:value})"
                            />
                            <span class="text-nowrap"> of {{ props.total }} entries </span>
                        </div>
                        <div>
                            <b-pagination
                                :value="1"
                                :total-rows="props.total"
                                :per-page="pageLength"
                                first-number
                                last-number
                                align="right"
                                prev-class="prev-item"
                                next-class="next-item"
                                class="mt-1 mb-0"
                                @input="(value)=>props.pageChanged({currentPage:value})"
                            >
                                <template #prev-text>
                                    <feather-icon
                                        icon="ChevronLeftIcon"
                                        size="18"
                                    />
                                </template>
                                <template #next-text>
                                    <feather-icon
                                        icon="ChevronRightIcon"
                                        size="18"
                                    />
                                </template>
                            </b-pagination>
                        </div>
                    </div>
                </template>
            </vue-good-table>
        </div>

        <div v-else class="loading-user">
            <img src="../../../../assets/images/logo/logo.png" alt="">
            <div class="load-6">
                <div class="letter-holder">
                    <div class="l-1 letter">P</div>
                    <div class="l-2 letter">l</div>
                    <div class="l-3 letter">e</div>
                    <div class="l-4 letter">a</div>
                    <div class="l-5 letter">s</div>
                    <div class="l-6 letter mr-1">e</div>
                    <div class="l-7 letter">W</div>
                    <div class="l-8 letter">a</div>
                    <div class="l-9 letter">i</div>
                    <div class="l-10 letter">t</div>
                    <div class="l-11 letter">.</div>
                    <div class="l-12 letter">.</div>
                    <div class="l-13 letter">.</div>
                </div>
            </div>
        </div>
    
        <b-modal
            id="modal-prevent-closing"
            ref="my-modal"
            :title="getModalTitle()"
            centered
            ok-title="Submit"
            cancel-variant="outline-secondary"
            @show="resetModal"
            @hidden="resetModal"
            @ok="handleOk"
        >
            <form
                ref="form"
                @submit.stop.prevent="handleSubmit"
            >
                <b-form-group
                    :state="nameState"
                    label="First Name"
                    label-for="name-input"
                >
                    <b-form-input
                        id="name-input"
                        v-model="modify.first_name"
                        :state="nameState"
                    />
                </b-form-group>
                <b-form-group
                    :state="nameState"
                    label="Last Name"
                    label-for="name-input"
                >
                    <b-form-input
                        id="name-input"
                        v-model="modify.last_name"
                        :state="nameState"
                    />
                </b-form-group>
                <b-form-group
                    :state="nameState"
                    label="Email"
                    label-for="name-input"
                >
                    <b-form-input
                        id="name-input"
                        v-model="modify.email"
                        :state="nameState"
                    />
                </b-form-group>
                <b-form-group
                    :state="nameState"
                    label="Password"
                    label-for="name-input"
                >
                    <b-form-input
                        id="name-input"
                        v-model="modify.password"
                        :state="nameState"
                    />
                </b-form-group>
                <b-form-group
                    :state="nameState"
                    label="Confirm Password"
                    label-for="name-input"
                >
                    <b-form-input
                        id="name-input"
                        v-model="modify.password2"
                        :state="nameState"
                    />
                </b-form-group>
            </form>
        </b-modal>

        <b-modal
            id="modal-delete"
            ref="modal-delete"
            centered
            modal-class="modal-danger"
            title="Are you sure?"
        >
            <b-card-text class="border-2">
                <span>If you delete, you won't be able to revert this action!!</span>
            </b-card-text>
    
            <template #modal-footer>
                <div class="w-100">
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="danger"
                        class="float-right"
                        @click="confirmDelete()"
                    >
                        Accept
                    </b-button>
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        class="float-right mr-1"
                        @click="$refs['modal-delete'].toggle('#delete-btn')"
                    >
                        Cancel
                    </b-button>
                </div>
            </template>
        </b-modal>
    </b-card>
</template>
  
  <script>
  
import {
BAvatar,BButton, BBadge, BPagination, BFormGroup, BFormInput, BFormSelect, BDropdown, BDropdownItem, BCard,
BModal,VBModal,BCardText, BFormTextarea
} from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import store from '@/store/index';
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import {mapState} from "vuex";
import ToastificationContent from "@core/components/toastification/ToastificationContent";

export default {
    components: {
        VueGoodTable,
        BAvatar,
        BCard,
        BBadge,
        BButton,
        BPagination,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BDropdown,
        BDropdownItem,
        BModal,
        vSelect,
        BCardText,
        BFormTextarea
    },

    directives: {
        'b-modal': VBModal,
        Ripple
    },

    data() {
        return {
            nameState: null,
            addNewMode: true,
            updateStatusMode: false,
            popupActive: true,
            status: {
                id: "",
                status: ""
            },
            modify:{
                id: null,
                first_name: null,
                last_name: null,
                email: null,
                password: null,
                password2: null
            },
            country_id: null,
            pageLength: 10,
            dir: false,
            columns: [
                {
                    label: '#',
                    field: 'index',
                    type:'number',
                    tdClass: 'text-center ',
                    thClass:'text-center'
                },
                {
                    label: 'Name',
                    field: 'fullName',
                    tdClass: 'text-center',
                    thClass:'text-center'
                },
                {
                    label: 'Username',
                    field: 'username',
                    tdClass: 'text-center',
                    thClass:'text-center'
                },
                {
                    label: 'Group',
                    field: 'group_name',
                    tdClass: 'text-center',
                    thClass:'text-center'
                },
                {
                    label: 'Email',
                    field: 'email',
                    tdClass: 'text-center',
                    thClass:'text-center'
                }
            ],
            showAdd: true,
            showEdit: true,
            showDelete: true,
            rows: [],
            searchTerm: ''
        }
    },

    computed: {
        ...mapState('admin', ["admins","adminError","adminLoading"]), 
    
        direction() {
            if (store.state.appConfig.isRTL) {
                this.dir = true
                return this.dir
            }
            this.dir = false
            return this.dir
        },
    },

    watch: {
        admins(newValue, oldValue) {
            this.rows = this.admins
        },
        adminError(newValue, oldValue) {},
        adminLoading(newValue, oldValue) {}
    },

    created() {
        this.$store.dispatch('admin/fetchAdminList')
        this.rows = this.admins
        let permissions=JSON.parse(localStorage.getItem('permissions'))
        if(this.showEdit||this.showDelete){
            this.columns.push({
                label: 'Action',
                field: 'action',
                tdClass: 'text-center',
                thClass:'text-center',
                sortable: false
            })
        }
    },

    methods:{
        checkFormValidity() {
            const valid = this.$refs.form.checkValidity()
            this.nameState = valid
            return valid
        },

        resetModal() {
            this.nameState = null
        },

        handleOk(bvModalEvt) {
            bvModalEvt.preventDefault()
            this.handleSubmit()
        },

        async handleSubmit() {
            if (!this.checkFormValidity()) {
                return
            }
            if(this.addNewMode){
                await this.$store.dispatch('admin/addAdmin',{
                    'email':this.modify.email,
                    'first_name':this.modify.first_name,
                    'last_name':this.modify.last_name,
                    'password':this.modify.password,
                    'password2':this.modify.password2,
                }).then(result=>{
                    if(result.code == 200) {
                        this.$toast({
                            component: ToastificationContent,
                            position: 'top-right',
                            props: {
                                title: `Success`,
                                icon: 'ThumbsUpIcon',
                                variant: 'success',
                                text: result.response,
                            }
                        })
                        this.modify.id= null,
                        this.modify.first_name= null,
                        this.modify.last_name= null,
                        this.modify.email= null,
                        this.modify.password= null,
                        this.modify.password2= null
                    } else {
                        this.$toast({
                            component: ToastificationContent,
                            position: 'top-right',
                            props: {
                                title: `Error`,
                                icon: 'ThumbsDownIcon',
                                variant: 'error',
                                text: result.message,
                            }
                        })
                    }
                })
            }else{
                await this.$store.dispatch('admin/updateAdmin',this.modify).then(result=> {
                    if(result.code == 200) {
                        this.$toast({
                            component: ToastificationContent,
                            position: 'top-right',
                            props: {
                                title: `Success`,
                                icon: 'ThumbsUpIcon',
                                variant: 'success',
                                text: result.response,
                            }
                        })

                        this.modify.id= null,
                        this.modify.first_name= null,
                        this.modify.last_name= null,
                        this.modify.email= null,
                        this.modify.password= null,
                        this.modify.password2= null,
                        this.addNewMode = true
                    } else {
                        this.$toast({
                            component: ToastificationContent,
                            position: 'top-right',
                            props: {
                                title: `Error`,
                                icon: 'ThumbsDownIcon',
                                variant: 'error',
                                text: result.response,
                            }
                        })
                    }
                })
            }
            this.$nextTick(() => {
                this.$refs['my-modal'].toggle('#toggle-btn')
            })
        },

        statusModal(status) {
            this.updateStatusMode = true;
            this.status.id = status.personal_info.profile_id
            this.status.status = status.personal_info.profile_status
        },

        edit (data) {
            this.addNewMode = false;
            this.modify.id=data.id
            this.modify.first_name = data.first_name;
            this.modify.last_name = data.last_name;
            this.modify.email=data.email;
            this.modify.password=data.password;
            this.modify.password2=data.password2;
        },

        initValues(){
            this.modify={
                id: null,
                name: null,
                district: null,
                country: null,
            }
            this.country_id=null
        },

        deleteType(data){
            this.modify.id=data.id
        },

        async confirmDelete(){
            await this.$store.dispatch("admin/deleteAdmin", this.modify.id).then(result=>{
                if(result.code == 200) {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: `Success`,
                            icon: 'ThumbsUpIcon',
                            variant: 'success',
                            text: result.message,
                        }
                    })
                } else {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: `Erroe`,
                            icon: 'ThumbsDownIcon',
                            variant: 'error',
                            text: result.message,
                        }
                    })
                }
            })
            this.$nextTick(() => {
                this.$refs['modal-delete'].toggle('#delete-btn')
            })
        },

        getModalTitle(){
            return (this.addNewMode? 'Add New ' : 'Update ') +'Admin'
        }
    }
}
</script>
  
<style lang="scss" >
.no-outline{
    border:none !important;
}

.f20{
    font-size: 8px !important;
}

.vs__dropdown-menu {
  height: 200px;
}

@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';

</style>